// import node module libraries
import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
	Image,
	Card,
	Button,
	Row,
	Col,
	ProgressBar,
	ListGroup,
	Badge,
	Tooltip,
	OverlayTrigger
} from 'react-bootstrap';

// import custom components
import Ratings from 'components/marketing/common/ratings/Ratings';
import LevelIcon from 'components/marketing/common/miscellaneous/LevelIcon';
import GKTippy from 'components/elements/tooltips/GKTippy';

// import translation hook
import { useTranslation } from 'react-i18next';

// import utility file
import { numberWithCommas } from 'helper/utils';

const FlowCard = ({
	item,
	config,
	free,
	viewby,
	showprogressbar,
	onClick,
	extraclass,
	link
}) => {
	const { t } = useTranslation();

	/** Used in Course Index, Course Category, Course Filter Page, Student Dashboard etc...  */
	const GridView = () => {
		return (
			<Card className={`mb-4 card-hover ${extraclass} d-flex flex-column h-100`}>
				<div className="position-relative">
					<Link to={link}>
						<Image
							src={item.image ? item.image : '/assets/images/placeholder.png'}
							alt=""
							className="card-img-top rounded-top-md"
						/>
					</Link>
					<div className="position-absolute top-0 end-0 p-3">
						{item.premium ? <Badge bg="primary">{t('Premium')}</Badge> : <Badge bg="secondary">{t('Standard')}</Badge>}
					</div>
				</div>
				{/* Card body  */}
				<Card.Body className="d-flex flex-column" style={{ minHeight: '280px' }}>
					<span className="text-muted fs-6">
					{item.category && 
						item.category === 'store' ? 'Marktplatz' : 
						item.category === 'shopsystem' ? 'Shop & Warenwirtschaft' : 
						item.category === 'other' ? 'Weitere Connectoren' : 
						item.category
					}
					</span>
					<h3 className="h4 mb-2 text-truncate-line-2 ">
						{item.title}
					</h3>
					<span className="text-muted fs-6">
						{item.description}
					</span>
					<ListGroup as="ul" bsPrefix="list-inline" className="mb-3 mt-2 text-muted fs-6">
						{item.features && item.features.map((feature, index) => (
							<ListGroup.Item as="li" bsPrefix="" key={index}>
								<i className="fe fe-arrow-right"></i>
								{t(feature)}
							</ListGroup.Item>
						))}
					</ListGroup>
					{/* List inline */}
					<div className="mt-auto">
						<span className="text-muted fs-6">{t('Costs')}</span>
						<div className={`lh-1`}>
							{item.price - item.discount > 0 ?
								(<>
									<span className="text-dark fw-bold fs-3">
										{item.price - item.discount}
									</span><span className="h3 mb-0 fw-bold">€</span>
									<del className={`fs-6 text-muted ${item.discount <= 0 ? 'd-none' : ''} `}>{item.price}€</del>
									<span className="align-self-end fs-6 mb-1 ms-2 toggle-price-content">
										/{t('Month')}
									</span>
								</>) :
								(<span className="text-dark fw-bold fs-3">{t('for free')}</span>)
							}
						</div>
					</div>
				</Card.Body>
				{/* Card Footer */}
				<Card.Footer className="mt-auto">
					<Row className="align-items-center g-0">
						<Col xs="auto">
							<span className="text-muted fs-6">{t('Status')}</span>
							<OverlayTrigger
								placement="top"
								overlay={
									<Tooltip>
										{item.status === 'productive' ? t('This connector is already in productive use') :
											item.status === 'development' ? t('We are working hard on this connector') :
												item.status === 'planning' ? t('The development is planned') :
													item.status === 'check' ? t('This Flow has to be checked') :
														t('No Status found')}
									</Tooltip>}
							>
								<h5 className="h5 mb-0 text-muted">
									{item.status &&
										item.status === 'active' ? t('Active') :
											item.status === 'development' ? t('In development') :
												item.status === 'planning' ? t('In planning') :
													item.status === 'check' ? t('Please Check') :
														item.status
									}
								</h5>
							</OverlayTrigger>
						</Col>
					</Row>
					<span className={`${showprogressbar ? '' : 'd-none'}`}>
						{' '}
						<ProgressBar
							variant={
								item.status === 'active' ? 'success' :
									item.status === 'development' ? 'secondary' :
										'secondary'
							}
							now={item.progress}
							className="mt-3"
							style={{ height: '5px' }}
						/>
					</span>
				</Card.Footer>
			</Card>
		);
	};

	/** Used in Dashboard -> Marketplace */
	const MarketplaceView = () => {
		return (
			<Card className={`mb-4 card-hover ${extraclass} d-flex flex-column h-100`}>
				<div className="position-relative">
					<Link to={link}>
						<Image
							src={item.image ? item.image : '/assets/images/placeholder.png'}
							alt=""
							className="card-img-top rounded-top-md"
						/>
					</Link>
					<div className="position-absolute top-0 end-0 p-3">
						{item.premium ? <Badge bg="primary">{t('Premium')}</Badge> : <Badge bg="secondary">{t('Standard')}</Badge>}
					</div>
				</div>
				{/* Card body  */}
				<Card.Body className="d-flex flex-column" style={{ minHeight: '280px' }}>
					<span className="text-muted fs-6">
						{item.category &&
							item.category === 'store' ? 'Marktplatz' :
							item.category === 'shopsystem' ? 'Shop & Warenwirtschaft' :
								item.category === 'other' ? 'Weitere Connectoren' :
									item.category
						}
					</span>
					<h3 className="h4 mb-2 text-truncate-line-2 ">
						<Link to={item.link} className="text-inherit">
							{item.title ? item.title : ''}
						</Link>
					</h3>
					<span className="text-muted fs-6">
						{item.description ? item.description : ''}
					</span>
					<ListGroup as="ul" bsPrefix="list-inline" className="mb-3 mt-2 text-muted fs-6">
						{item.features && item.features.map((feature, index) => (
							<ListGroup.Item as="li" bsPrefix="" key={index}>
								<i className="fe fe-arrow-right"></i>
								{feature}
							</ListGroup.Item>
						))}
					</ListGroup>
					{/* List inline */}
					<div className="mt-auto">
						<span className="text-muted fs-6">{t('Costs')}</span>
						<div className={`lh-1`}>
							{item.price - item.discount > 0 ?
								(<>
									<span className="text-dark fw-bold fs-3">
										{item.price - item.discount}
									</span><span className="h3 mb-0 fw-bold">€</span>
									<del className={`fs-6 text-muted ${item.discount <= 0 ? 'd-none' : ''} `}>{item.price}€</del>
									<span className="align-self-end fs-6 mb-1 ms-2 toggle-price-content">
										/{t('Month')}
									</span>
								</>) :
								(<span className="text-dark fw-bold fs-3">{t('for free')}</span>)
							}
						</div>
					</div>
				</Card.Body>
				{/* Card Footer */}
				<Card.Footer className="mt-auto">
					<Row className="align-items-center g-0">
						{showprogressbar ? (
							<>
								<Col xs="auto">
									<span className="text-muted fs-6">{t('Status')}</span>
									<OverlayTrigger
										placement="top"
										overlay={
											<Tooltip>
												{item.status === 'productive' ? t('This connector is already in productive use') :
													item.status === 'development' ? t('We are working hard on this connector') :
														item.status === 'planning' ? t('The development is planned') :
															t('No Status found') }
											</Tooltip>}
									>
										<h5 className="h5 mb-0 text-muted">
											{item.status &&
												item.status === 'productive' ? t('Productive') :
													item.status === 'development' ? t('In development') :
														item.status === 'planning' ? t('In planning') :
															item.status
											}
										</h5>
									</OverlayTrigger>
								</Col>
								<ProgressBar
									variant={
										item.status === 'productive' ? 'success' :
											item.status === 'development' ? 'secondary' :
												'secondary'
									}
									now={item.progress}
									className="mt-3"
									style={{ height: '5px' }}
								/>
							</>
						) : (
							<div className="d-grid gap-2">
									<Button className="rounded" variant="light" size="sm" 
										onClick={() => {
											if (onClick) {
												onClick(item);
											}
										}}
									>
										{t('Add now')}
									</Button>
							</div>
						)}
					</Row>
				</Card.Footer>
			</Card>
		);
	};

	/** Used in Dashboard -> Marketplace */
	const ConfigView = () => {
		return (
			<Card className={`mb-4 card-hover ${extraclass} d-flex flex-column h-100`}>
				<div className="position-relative">
					<Link to={`/dashboard/marketplace/flows/${config.id}`}>
						<Image
							src={item.image ? item.image : '/assets/images/placeholder.png'}
							alt=""
							className="card-img-top rounded-top-md"
						/>
					</Link>
					<div className="position-absolute top-0 end-0 p-3">
						{item.premium ? <Badge bg="primary">{t('Premium')}</Badge> : <Badge bg="secondary">{t('Standard')}</Badge>}
					</div>
				</div>
				{/* Card body  */}
				<Card.Body className="d-flex flex-column" style={{ minHeight: '280px' }}>
					<span className="text-muted fs-6">
						{item.category &&
							item.category === 'store' ? 'Marktplatz' :
							item.category === 'shopsystem' ? 'Shop & Warenwirtschaft' :
								item.category === 'other' ? 'Weitere Connectoren' :
									item.category
						}
					</span>
					<h3 className="h4 mb-2 text-truncate-line-2 ">
						<Link to={`/dashboard/marketplace/flows/${config.id}`} className="text-inherit">
							{item.title ? item.title : ''}
						</Link>
					</h3>
					<span className="text-muted fs-6">
						{item.description ? item.description : ''}
					</span>
					<ListGroup as="ul" bsPrefix="list-inline" className="mb-3 mt-2 text-muted fs-6">
						{item.features && item.features.map((feature, index) => (
							<ListGroup.Item as="li" bsPrefix="" key={index}>
								<i className="fe fe-arrow-right"></i>
								{feature}
							</ListGroup.Item>
						))}
					</ListGroup>
					{/* List inline */}
					<div className="mt-auto">
						<span className="text-muted fs-6">{t('Costs')}</span>
						<div className={`lh-1`}>
							{item.price - item.discount > 0 ?
								(<>
									<span className="text-dark fw-bold fs-3">
										{item.price - item.discount}
									</span><span className="h3 mb-0 fw-bold">€</span>
									<del className={`fs-6 text-muted ${item.discount <= 0 ? 'd-none' : ''} `}>{item.price}€</del>
									<span className="align-self-end fs-6 mb-1 ms-2 toggle-price-content">
										/{t('Month')}
									</span>
								</>) :
								(<span className="text-dark fw-bold fs-3">{t('for free')}</span>)
							}
						</div>
					</div>
				</Card.Body>
				{/* Card Footer */}
				<Card.Footer className="mt-auto">
					<Row className="align-items-center g-0">
						{showprogressbar ? (
							<>
								<div className="d-flex justify-content-between align-items-center">
									<div className="">
										<span className="text-muted fs-6">{t('Status')}</span>
										<OverlayTrigger
											placement="top"
											overlay={
												<Tooltip>
													{config.config_value.status === 'active' ? t('This Flow is active') :
														config.config_value.status === 'inactive' ? t('This Flow is inactive') :
															config.config_value.status === 'ready' ? t('This Flow is configured and ready to activate') :
																config.config_value.status === 'configuration' ? t('Complete this Flow configuration') :
																	config.config_value.status === 'new' ? t('This Flow is new and needs to be configured') :
																		config.config_value.status === 'paused' ? t('This Flow is paused') :
																			config.config_value.status === 'failed' ? t('This Flow has failed') :
																				t('This Flow status is undefined')}
												</Tooltip>}
										>
											<h5 className="h5 mb-0 text-muted">
												{config.config_value.status &&
													config.config_value.status === 'active' ? t('Active') :
														config.config_value.status === 'inactive' ? t('Inactive') :
															config.config_value.status === 'ready' ? t('Ready') :
																config.config_value.status === 'configuration' ? t('In configuration') :
																	config.config_value.status === 'new' ? t('New added') :
																		config.config_value.status === 'paused' ? t('Paused') :
																			config.config_value.status === 'failed' ? t('Failed') :
																				'Undefined'
												}
											</h5>
										</OverlayTrigger>
									</div>
									<div className="d-flex justify-content-end">
										<Link
											className="btn btn-light btn-sm rounded"
											to={`/dashboard/marketplace/flows/${config.id}`}
										>
											{t('Configure')}
										</Link>
									</div>
								</div>
								<ProgressBar
									variant={
										config.config_value.status === 'active' ? 'success' :
											config.config_value.status === 'inactive' ? 'secondary' :
												config.config_value.status === 'ready' ? 'primary' :
													config.config_value.status === 'configuration' ? 'warning' :
														config.config_value.status === 'new' ? 'secondary' :
															config.config_value.status === 'paused' ? 'primary' :
																config.config_value.status === 'failed' ? 'danger' :
																	'secondary'
									}
									now={config.config_value.progress}
									className="mt-3"
									style={{ height: '5px' }}
								/>
							</>
						) : (
							<div className="d-grid gap-2">
								<Button className="rounded" variant="light" size="sm">{t('Add now')}</Button>
							</div>
						)}
					</Row>
				</Card.Footer>
			</Card>
		);
	};

	return (
		<Fragment>
			{viewby === 'grid' ? (
				<GridView />
			) : viewby === 'marketplace' ? (
				<MarketplaceView />
			) : viewby === 'config' ? (
				<ConfigView />
			) : (
				<GridView />
			)}
		</Fragment>
	);
};

// Specifies the default values for props
FlowCard.defaultProps = {
	free: false,
	viewby: 'grid',
	showprogressbar: false,
	extraclass: '',
	link: '#'
};

// Typechecking With PropTypes
FlowCard.propTypes = {
	item: PropTypes.object.isRequired,
	free: PropTypes.bool,
	viewby: PropTypes.string,
	showprogressbar: PropTypes.bool,
	extraclass: PropTypes.string,
	link: PropTypes.string
};

export default FlowCard;
